import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { BackgroundImage, Video } from '$partials/media';
import { IconUniPlan } from '$partials/icon/icons';
import { Navigation } from './navigation.jsx';
import { Search } from './search.jsx';
import { useLanguage } from '$contexts/language.jsx';
import { LocaleSelector } from '$partials/global';


/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const SideBar = ({
    bgColour    = null,
    bgFixed     = false,
    bgImage     = null,
    bgVideo     = null,
    children    = null,
    className   = null,
    hasMedia    = null,
    isHeader    = true,
    localePages = null,
    navigation  = null,
    search      = null
}) => {
    // references
    const logoRef = useRef();
    const navigationButtonRef = useRef();
    const searchButtonRef = useRef();
    const localeSelectorRef = useRef();
    const sideBarRef = useRef();

    // state
    const [activeDrawer, setActiveDrawer] = useState(null);
    const [colourLogo, setColourLogo] = useState(hasMedia ? 'white' : 'red-orange');
    const [colourNavigation, setColourNavigation] = useState(hasMedia ? 'white' : 'black');
    const [colourSearch, setColourSearch] = useState(hasMedia ? 'white' : 'black');
    const [colourLocale, setColourLocale] = useState(hasMedia ? 'white' : 'black');

    // methods
    const handleKeyPress = (e) => {
        if (e.key === 'Escape') {
            setActiveDrawer(null);
        }
    };

    const handleResizeScroll = () => {
        if (sideBarRef.current) {
            const sideBarBottom = sideBarRef.current.getBoundingClientRect()?.bottom;

            if (logoRef.current) {
                const logoBottom = logoRef.current.getBoundingClientRect().bottom;

                if (sideBarBottom <= logoBottom) {
                    setColourLogo('red-orange');
                } else {
                    setColourLogo(hasMedia ? 'white' : 'red-orange');
                }
            }

            if (navigationButtonRef.current) {
                const navigationButtonBounds = navigationButtonRef.current.getBoundingClientRect();
                const navigationButtonMiddle = (navigationButtonBounds.top + (navigationButtonBounds.height/2));

                if (sideBarBottom <= navigationButtonMiddle) {
                    setColourNavigation('black');
                } else {
                    setColourNavigation('white');
                }
            }

            if (searchButtonRef.current) {
                const searchButtonBounds = searchButtonRef.current.getBoundingClientRect();
                const searchButtonMiddle = (searchButtonBounds.top + (searchButtonBounds.height/2));

                if (sideBarBottom <= searchButtonMiddle) {
                    setColourSearch('black');
                } else {
                    setColourSearch('white');
                }
            }

            if (localeSelectorRef.current) {
                const localeSelectorBounds = localeSelectorRef.current.getBoundingClientRect();
                const localeSelectorMiddle = (localeSelectorBounds.top + (localeSelectorBounds.height/2));

                if (sideBarBottom <= localeSelectorMiddle) {
                    setColourLocale('black');
                } else {
                    setColourLocale('white');
                }
            }
        }
    };

    const throttle = (fn, delay) => {
        let lastTime = 0;

        return () => {
            const now = Date.now();

            if (now - lastTime >= delay) {
                fn();
                lastTime = now;
            }
        };
    };

    const toggleDrawer = (e, name) => {
        e.preventDefault();

        if (activeDrawer === name) {
            setActiveDrawer(null);
        } else {
            setActiveDrawer(name);
        }
    };

    // hooks
    const { current: localeCurrent } = useLanguage();

    useEffect(() => {
        window.addEventListener('keyup', handleKeyPress, { passive: true });

        return () => {
            window.removeEventListener('keyup', handleKeyPress);
        };
    }, []);

    useEffect(() => {
        if (!isHeader || !hasMedia) {
            return;
        }

        const _handleResizeScroll = throttle(handleResizeScroll, 100);

        window.addEventListener('resize', _handleResizeScroll, { passive: true });
        window.addEventListener('scroll', _handleResizeScroll, { passive: true });

        return () => {
            window.removeEventListener('resize', _handleResizeScroll);
            window.removeEventListener('scroll', _handleResizeScroll);
        };
    }, []);

    // render
    return (
        <div
            className={`side-bar${className ? ' ' + className : ''}`}
            ref={sideBarRef}
            style={{
                '--bgColour': bgColour ? bgColour : '#FFFFFF',
                backgroundColor:'var(--bgColour)'
            }}>

            {(bgImage && !bgVideo) &&
                <BackgroundImage
                    className={`${bgFixed ? 'fixed' : 'absolute'} bg-overlay top-0 left-0 col-12 height-100vh`}
                    {...bgImage} />
            }

            {bgVideo && (
                <Video
                    autoPlay={true}
                    className={`height-100 width-100 object-cover object-center bg-overlay${bgFixed ? ' fixed col-12' : ' relative'}`}
                    controls={false}
                    disablePictureInPicture={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    src={bgVideo.url} />
            )}

            {!isHeader && (
                <div className='side-bar__aside side-bar__aside--empty' />
            )}

            {isHeader && (
                <header className='side-bar__aside z4'>
                    <div
                        className='hide md-show fixed bottom-0'
                        ref={localeSelectorRef}>
                        <div
                            className='flex items-end'>
                            <LocaleSelector
                                colour={colourLocale}
                                pages={localePages} />
                        </div>
                    </div>
                    <div className='fixed flex flex-col items-center'>
                        <Navigation
                            className={`transition-colour ${colourNavigation}`}
                            isOpen={activeDrawer === 'navigation'}
                            localePages={localePages}
                            ref={navigationButtonRef}
                            toggleDrawer={toggleDrawer}
                            { ...navigation } />

                        <Search
                            className={`transition-colour ${colourSearch}`}
                            isOpen={activeDrawer === 'search'}
                            ref={searchButtonRef}
                            toggleDrawer={toggleDrawer}
                            { ...search } />

                        <a
                            className={`block line-height-0 transition-colour ${colourLogo}`}
                            href={`/${localeCurrent === 'en' ? '' : localeCurrent}`}
                            ref={logoRef}>
                            <IconUniPlan />
                        </a>
                    </div>
                </header>
            )}

            <div className='side-bar__content'>
                {children}
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    bgColour: PropTypes.string,
    bgFixed : PropTypes.bool,
    bgImage : PropTypes.object,
    bgVideo : PropTypes.object,
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array
    ]),
    hasMedia   : PropTypes.bool,
    isHeader   : PropTypes.bool,
    className  : PropTypes.string,
    localePages: PropTypes.array,
    navigation : PropTypes.object,
    search     : PropTypes.object,
};

SideBar.propTypes = propTypes;

export { SideBar };

export default SideBar;
